import React, { useMemo, useState } from "react";
import { Trash2, Clock, Pencil, Check } from "lucide-react";
import {
  Badge,
  type BadgeVariant,
  ResponsiveDataTable,
  type ResponsiveColumn,
} from "../../../../ui/index.ts";
import styles from "./timesheet-data-table.module.css";
import { TimesheetEntry } from "../../types.ts";
import { TaskCategoryLabel } from "../../../../shared/task-category-label/task-category-label.tsx";
import { EditEntryModal } from "../edit-entry-modal/edit-entry-modal.tsx";
import { isRejectedEditableEntry } from "../../utils/timesheet-entry-flags.ts";

type Props = {
  entries: TimesheetEntry[];
  selectedDate: Date;
  onDelete: (id: string) => void;
  onEdit: (updated: TimesheetEntry) => void;
  isSubmitted?: boolean;
  /** Past dates (before yesterday): table is read-only, no row actions. */
  viewOnly?: boolean;
};

/** Row shape for the table (grouped project display + optional code from future API). */
type TimesheetTableRow = TimesheetEntry & {
  showProject: boolean;
  projectCode?: string;
} & Record<string, unknown>;

const STATUS_BADGE_MAP: Record<
  string,
  { label: string; className: string; showIcon?: boolean }
> = {
  inprogress: { label: "In-progress", className: "badge--warning" },
  completed: { label: "Completed", className: "badge--success" },
};

const getTotalTime = (entries: TimesheetEntry[]) => {
  const totalMins = entries.reduce(
    (acc, e) =>
      acc + Number(e.hours) * 60 + (Number(e.minutes) || 0),
    0,
  );
  return { hours: Math.floor(totalMins / 60), mins: totalMins % 60 };
};

function approvalStatusToVariant(raw: string | undefined): BadgeVariant {
  const s = (raw ?? "").toUpperCase();
  if (s === "APPROVED" || s === "ACCEPTED") return "success";
  if (s === "REJECTED" || s === "DECLINED") return "danger";
  if (s === "PENDING" || s.includes("PENDING")) return "warning";
  return "neutral";
}

function formatApprovalStatusLabel(raw: string | undefined): string {
  if (!raw?.trim()) return "—";
  return raw
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isTruthyAutoApproved(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value !== "string") return false;
  return value.trim().toLowerCase() === "true";
}

function formatPhase(phase: TimesheetEntry["phase"]): string {
  if (!phase) return "—";
  if (phase === "UAT") return "UAT";
  return phase
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export const TimesheetDataTable = ({
  entries,
  selectedDate,
  onDelete,
  onEdit,
  isSubmitted = false,
  viewOnly = false,
}: Props) => {
  const [editingEntry, setEditingEntry] = useState<TimesheetEntry | null>(null);
  const dayEntries = entries;

  const tableData = useMemo(() => {
    const grouped = dayEntries.reduce<Record<string, TimesheetEntry[]>>(
      (acc, entry) => {
        if (!acc[entry.project]) acc[entry.project] = [];
        acc[entry.project].push(entry);
        return acc;
      },
      {},
    );
    return Object.entries(grouped).flatMap(([, projectEntries]) =>
      projectEntries.map((entry, idx) => ({
        ...entry,
        showProject: idx === 0,
      })),
    ) as TimesheetTableRow[];
  }, [dayEntries]);

  const showActionsColumn =
    !viewOnly &&
    (!isSubmitted || dayEntries.some((e) => isRejectedEditableEntry(e)));

  const hideActionsColumn = !showActionsColumn;

  const columns: ResponsiveColumn<TimesheetTableRow>[] = useMemo(
    () =>
      [
        {
          id: "project",
          header: "Project",
          sortable: false,
          render: (row) =>
            row.showProject ? (
              <div className={styles.projectCell}>
                <span className={styles.projectName}>{row.project}</span>
                {row.projectCode ? (
                  <span className={styles.projectCode}>{row.projectCode}</span>
                ) : null}
              </div>
            ) : (
              <div />
            ),
        },
        {
          id: "workStream",
          header: "Work Stream",
          sortable: false,
          render: (row) => row.workStream || "—",
        },
        {
          id: "phase",
          header: "Phase",
          sortable: false,
          render: (row) => {
            const phaseLabel = row.phaseName || formatPhase(row.phase);
            return <span className={row.phaseName || row.phase ? styles.phasePill : styles.mutedValue}>{phaseLabel}</span>;
          },
        },
        {
          id: "category",
          header: "Category",
          sortable: false,
          render: (row) => <TaskCategoryLabel variant={row.category} />,
        },
        {
          id: "description",
          header: "Task Description",
          sortable: false,
          render: (row) => (
            <div className={styles.descriptionCell}>{row.description}</div>
          ),
        },
        {
          id: "time",
          header: "Time",
          sortable: false,
          nowrap: true,
          render: (row) => (
            <span className={styles.timeValue}>
              <strong>{row.hours}</strong>
              <span className={styles.timeUnit}>h</span>
              <strong>{row.minutes}</strong>
              <span className={styles.timeUnit}>m</span>
            </span>
          ),
        },
        {
          id: "status",
          header: " Task Status",
          sortable: false,
          nowrap: true,
          render: (row) => {
            const key = row.status.toLowerCase().replace(/-/g, "");
            const status = STATUS_BADGE_MAP[key] ?? {
              label: row.status,
              className: "badge--warning",
            };

            return (
              <div className={styles.statusWrapper}>
                <span className={status.className}>
                  {status.showIcon && <Check size={12} aria-hidden />}
                  {status.label}
                </span>
              </div>
            );
          },
        },
        ...(isSubmitted
          ? ([
            {
              id: "approvalStatus",
              header: "Status",
              sortable: false,
              nowrap: true,
              mobileLabel: "Approval status",
              render: (row) => {
                if (
                  isTruthyAutoApproved(row.isAutoApproved) ||
                  isTruthyAutoApproved(row.isAutoapprovde)
                ) {
                  return <Badge variant="success">Auto-Approved</Badge>;
                }

                const raw =
                  row.approvalStatus ?? row.approverStatus ?? "";
                return (
                  <Badge
                    variant={approvalStatusToVariant(raw)}

                  >
                    {formatApprovalStatusLabel(raw)}
                  </Badge>
                );
              },
            },
          ] as ResponsiveColumn<TimesheetTableRow>[])
          : []),
        {
          id: "actions",
          header: "Actions",
          sortable: false,
          nowrap: true,
          render: (row) =>
            !viewOnly &&
              (!isSubmitted || isRejectedEditableEntry(row)) ? (
              <div className={styles.actionsContainer}>
                <button
                  type="button"
                  className={styles.editBtn}
                  onClick={() => setEditingEntry(row)}
                  title="Edit entry"
                >
                  <Pencil size={15} />
                </button>
                {!isRejectedEditableEntry(row) ? (
                  <button
                    type="button"
                    className={styles.deleteBtn}
                    onClick={() => onDelete(row.id)}
                    title="Remove entry"
                  >
                    <Trash2 size={15} />
                  </button>
                ) : null}
              </div>
            ) : (
              <span className={styles.actionsPlaceholder} aria-hidden>
                —
              </span>
            ),
        },
      ].filter((col) => showActionsColumn || col.id !== "actions"),
    [showActionsColumn, isSubmitted, viewOnly, onDelete],
  );

  const tableFooter = useMemo(
    () => renderFooter(dayEntries, hideActionsColumn, isSubmitted),
    [dayEntries, hideActionsColumn, isSubmitted],
  );

  const mobileSummary = useMemo(
    () => renderMobileSummary(dayEntries),
    [dayEntries],
  );

  if (dayEntries.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Clock size={32} className={styles.emptyIcon} />
        <p className={styles.emptyTitle}>No entries for this day</p>
        <p className={styles.emptySubtitle}>
          {viewOnly
            ? "There are no time entries recorded for this date."
            : "Use the form above to add your first time entry."}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.tableWrapper}>
        <ResponsiveDataTable<TimesheetTableRow>
          pagination={false}
          className={styles.responsiveRoot}
          columns={columns}
          data={tableData}
          aria-label="Timesheet entries"
          tableFooter={tableFooter}
          mobileSummary={mobileSummary}
        />
      </div>
      {editingEntry && (
        <EditEntryModal
          key={editingEntry.id}
          entry={editingEntry}
          selectedDate={selectedDate}
          onClose={() => setEditingEntry(null)}
          onSave={(updated) => {
            onEdit(updated);
            setEditingEntry(null);
          }}
        />
      )}
    </div>
  );
};

function renderFooter(
  entries: TimesheetEntry[],
  hideActionsColumn: boolean,
  isSubmitted: boolean,
) {
  const total = getTotalTime(entries);
  const hasActions = !hideActionsColumn;
  const columnCount =
    7 + (isSubmitted ? 1 : 0) + (hasActions ? 1 : 0);
  const footerTailColSpan = Math.max(1, columnCount - 6);

  return (
    <tr className={styles.footerRow}>
      <td colSpan={5} className={styles.footerLeft}>
        Daily Total
      </td>
      <td className={styles.footerCenter}>
        <div className={styles.footerTimeWrapper}>
          {total.hours > 0 && (
            <>
              <strong>{total.hours}</strong>
              <span>h</span>
            </>
          )}
          <strong>{total.mins}</strong>
          <span>m</span>
        </div>
      </td>
      <td colSpan={footerTailColSpan} className={styles.footerRight}>
        {entries.length} {entries.length === 1 ? "entry" : "entries"}
      </td>
    </tr>
  );
}

function renderMobileSummary(entries: TimesheetEntry[]) {
  const total = getTotalTime(entries);
  return (
    <div className={styles.mobileFooter}>
      <div className={styles.mobileFooterRow}>
        <span className={styles.footerLeft}>Daily Total</span>
        <span className={styles.footerTimeWrapper}>
          {total.hours > 0 && (
            <>
              <strong>{total.hours}</strong>
              <span>h</span>
            </>
          )}
          <strong>{total.mins}</strong>
          <span>m</span>
        </span>
      </div>
      <p className={styles.mobileFooterMeta}>
        {entries.length} {entries.length === 1 ? "entry" : "entries"}
      </p>
    </div>
  );
}
